import type { CartProps, LineItem } from 'tsconfig/types';
import { BrandId, CardType } from '../enums/enums';
import { globals } from '../globals';
import {
  CART_BULK_DEAL_DISCOUNT,
  getInventoryLimitCheck,
  getMaxLimitCheck,
  handleDLMiniCartClick,
  hasBulkDealDiscount,
  ITEMCLASSIFICATION_TYPE_GIFT,
} from 'common-ui';

/**
 * Returns the image URL for a gift card or initial deposit card based on brand and card type.
 *
 * @param {string} brandId - The ID of the brand.
 * @param {string} cardType - The type of the card (e.g., gift card or deposit).
 * @returns {string} The constructed image URL.
 */
export const getGiftAndDepositeCardImage = (brandId: string, cardType: string): string => {
  const normalizedBrandId = [BrandId.LAW, BrandId.WPE, BrandId.LWNZ].includes(brandId as BrandId)
    ? BrandId.LW
    : brandId;
  const imageName = cardType === CardType.DEPOSIT ? 'initialdeposit' : 'giftcard';

  return `${globals.publicHost}/images/${globals.country}/en/product/${normalizedBrandId}-${imageName}.png`;
};

/**
 * Handles a click event on a link element and prevents default navigation
 * if the user is already on the target page. Also calls an optional close handler.
 *
 * @param {React.MouseEvent<HTMLAnchorElement>} e - The click event.
 * @param {string} href - The target URL for navigation.
 * @param {Function} [onClose=() => {}] - Optional callback invoked after the check.
 */
export const handleRedirectionClick = (
  e: React.MouseEvent<HTMLAnchorElement>,
  href: string,
  onClose: () => void,
): void => {
  const currentUrl = window.location.pathname;
  if (currentUrl === href) {
    e.preventDefault();
  }
  onClose();
};

/**
 * Sends a mini cart click event to the data layer if the event string is not empty.
 *
 * @param {string} clickEvent - The name or identifier of the click event.
 */
export const handleMiniCartClickEvents = (clickEvent: string): void => {
  clickEvent !== '' && handleDLMiniCartClick('miniCartClick', clickEvent);
};

export const getSelectedItem = (cartItems: CartProps, selectedItemCode: string): LineItem[] => {
  const items = cartItems.lineItems?.filter(
    (item: LineItem) =>
      item.sku.itemCode === selectedItemCode &&
      !item.itemClassifications?.some((classification: string) =>
        classification.toLowerCase().includes(ITEMCLASSIFICATION_TYPE_GIFT),
      ),
  );
  return items?.length > 1 ? [items[items.length - 1]] : items;
};

export const shouldShowCheckoutButton = (cartItems: CartProps): boolean => {
  const inventoryLimit = getInventoryLimitCheck(cartItems.lineItems ?? []);
  const maxLimit = getMaxLimitCheck(cartItems.lineItems ?? []);
  return !(inventoryLimit || maxLimit);
};

export const isFreeGift = (cartItems: CartProps, isCartContainsBulkDeals: boolean, useNewCartAPI: boolean): boolean => {
  const freeItem = cartItems.lineItems?.filter((cartItem: LineItem) =>
    cartItem.itemClassifications?.some((classification: string) =>
      classification.toLowerCase().includes(ITEMCLASSIFICATION_TYPE_GIFT),
    ),
  );

  const bulkItems = cartItems.lineItems?.filter((item: LineItem) =>
    item.sku?.productName?.[0]?.value?.toLowerCase().includes('bulk'),
  );

  return freeItem?.length > 0 && (useNewCartAPI ? !isCartContainsBulkDeals : bulkItems.length === 0);
};

export const containsBulkDeals = (cartItems: CartProps): boolean =>
  cartItems.lineItems?.some(item => hasBulkDealDiscount(item, CART_BULK_DEAL_DISCOUNT)) ?? false;
